import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import * as react from "react";
import { FillMode, FixedMode } from "../../previewSizeManager.js";
/**
 * Creates the canvas for preview, sets the size based on the PreviewSizeManager's state, and tells the engine to resize
 * when the canvas changes natural size.
 */
export class PreviewAreaComponent extends react.Component {
    /**
     * Creates a new PreviewAreaComponent.
     * @param props The component props.
     */
    constructor(props) {
        super(props);
        this._canvasRef = react.createRef();
        this._onPointerDown = (event) => {
            if (!this._isDragSupported()) {
                return;
            }
            this.setState({
                isDragging: true,
                dragPointerStartX: event.clientX,
                dragPointerStartY: event.clientY,
                dragTranslateStartX: this.state.deltaX,
                dragTranslateStartY: this.state.deltaY,
            });
            event.preventDefault();
            this._canvasRef.current?.setPointerCapture(event.pointerId);
        };
        this._onPointerMove = (event) => {
            if (!this._isDragSupported()) {
                return;
            }
            if (this.state.isDragging) {
                const deltaX = this.state.dragTranslateStartX + (event.clientX - this.state.dragPointerStartX);
                const deltaY = this.state.dragTranslateStartY + (event.clientY - this.state.dragPointerStartY);
                this.setState({ deltaX, deltaY });
            }
        };
        this._onPointerUp = (event) => {
            if (!this._isDragSupported()) {
                return;
            }
            this.setState({ isDragging: false });
            this._canvasRef.current?.releasePointerCapture(event.pointerId);
        };
        this._onPointerCancel = () => {
            if (!this._isDragSupported()) {
                return;
            }
            this.setState({ isDragging: false });
        };
        this._onDoubleClick = () => {
            if (!this._isDragSupported()) {
                return;
            }
            this.setState({ isDragging: false, deltaX: 0, deltaY: 0 });
        };
        this.state = { isLoading: false, isDragging: false, dragPointerStartX: 0, dragPointerStartY: 0, dragTranslateStartX: 0, dragTranslateStartY: 0, deltaX: 0, deltaY: 0 };
        this._canvasResizeObserver = new ResizeObserver(() => {
            if (this.props.globalState.engine) {
                setTimeout(() => {
                    this.props.globalState.engine?.resize();
                }, 0);
            }
        });
        this._onResetRequiredObserver = this.props.globalState.onResetRequiredObservable.add(() => {
            this.forceUpdate();
        });
        this._onPreviewResetRequiredObserver = this.props.globalState.onPreviewResetRequiredObservable.add(() => {
            this.forceUpdate();
        });
        this._onModeChangedObserver = this.props.globalState.previewSizeManager.mode.onChangedObservable.add(() => {
            this.forceUpdate();
        });
        this._fixedWidthObserver = this.props.globalState.previewSizeManager.fixedWidth.onChangedObservable.add(() => {
            this.forceUpdate();
        });
        this._fixedHeightObserver = this.props.globalState.previewSizeManager.fixedHeight.onChangedObservable.add(() => {
            this.forceUpdate();
        });
        this._aspectRatioObserver = this.props.globalState.previewSizeManager.aspectRatio.onChangedObservable.add(() => {
            this.forceUpdate();
        });
    }
    /**
     * When the component mounts, attach the observer if the canvas is ready.
     */
    componentDidMount() {
        this._attachObserverToCanvas();
    }
    /**
     * When the component updates, ensure the observer is attached to the canvas.
     */
    componentDidUpdate() {
        this._attachObserverToCanvas();
    }
    /**
     * Lifecycle cleanup for observers.
     */
    componentWillUnmount() {
        this.props.globalState.onResetRequiredObservable.remove(this._onResetRequiredObserver);
        this.props.globalState.onPreviewResetRequiredObservable.remove(this._onPreviewResetRequiredObserver);
        this.props.globalState.previewSizeManager.mode.onChangedObservable.remove(this._onModeChangedObserver);
        this.props.globalState.previewSizeManager.fixedWidth.onChangedObservable.remove(this._fixedWidthObserver);
        this.props.globalState.previewSizeManager.fixedHeight.onChangedObservable.remove(this._fixedHeightObserver);
        this.props.globalState.previewSizeManager.aspectRatio.onChangedObservable.remove(this._aspectRatioObserver);
        if (this._canvasRef.current) {
            this._canvasResizeObserver.unobserve(this._canvasRef.current);
        }
    }
    /**
     * Renders the preview area and canvas.
     * @returns The preview JSX element.
     */
    render() {
        let divStyle;
        let previewStyle;
        switch (this.props.globalState.previewSizeManager.mode.value) {
            case FillMode:
                previewStyle = { width: "100%", height: "100%" };
                break;
            case FixedMode:
                divStyle = {
                    height: "100%",
                };
                previewStyle = {
                    width: this.props.globalState.previewSizeManager.fixedWidth.value + "px",
                    height: this.props.globalState.previewSizeManager.fixedHeight.value + "px",
                    position: "relative",
                    transform: `translate(${this.state.deltaX}px, ${this.state.deltaY}px)`,
                };
                break;
            case "aspectRatio":
                previewStyle = divStyle = { aspectRatio: this.props.globalState.previewSizeManager.aspectRatio.value };
                break;
        }
        return (_jsx(_Fragment, { children: _jsxs("div", { id: "preview", style: divStyle, onPointerDown: this._onPointerDown, onDoubleClick: this._onDoubleClick, onPointerMove: this._onPointerMove, onPointerUp: this._onPointerUp, onPointerCancel: this._onPointerCancel, children: [_jsx("div", { id: "sfe-preview-background", style: previewStyle, className: "preview-background-" + this.props.globalState.previewBackground, children: _jsx("canvas", { id: "sfe-preview-canvas", ref: this._canvasRef }) }), !this.props.globalState.smartFilter ? _jsx("div", { className: "waitPanel" + (this.state.isLoading ? "" : " hidden"), children: "Please wait, loading..." }) : _jsx(_Fragment, {})] }) }));
    }
    _attachObserverToCanvas() {
        const canvas = this._canvasRef.current;
        if (canvas) {
            this._canvasResizeObserver.observe(canvas);
        }
    }
    _isDragSupported() {
        return this.props.globalState.previewSizeManager.mode.value === FixedMode;
    }
}
//# sourceMappingURL=previewAreaComponent.js.map